import { useState, useEffect, useCallback } from "react";
import { getAuthHeader } from "../../lib/auth";
import { CopyInstructionsButton } from "../CopyInstructionsButton";
import { buildDaemonInstallInstructions } from "../../lib/copyInstructions";

// track:track-backlog--team-page-daemon-status-reuse-cc-http-poll — was a
// client-side Firestore onSnapshot on relay_presence, the same approach
// ControlCenter already replaced (see its own track:track-fix-daemon-online-check
// comment) because it was unreliable. Reusing CC's server-mediated
// GET /api/daemon/status poll instead — same data source, read via Admin SDK
// server-side rather than a client listener, and it's what CC already trusts.
function useDaemonStatus(workspaceId: string) {
  const [online, setOnline] = useState(false);
  const [lastHeartbeat, setLastHeartbeat] = useState<string | null>(null);

  const checkDaemonStatus = useCallback(async () => {
    try {
      const headers = await getAuthHeader();
      const res = await fetch("/api/daemon/status", { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setOnline(data.online === true);
      setLastHeartbeat(data.lastHeartbeat ?? null);
    } catch (err) {
      console.warn("[SystemTeamSection] daemon status poll", err);
      setOnline(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    checkDaemonStatus();
    const interval = setInterval(checkDaemonStatus, 15_000);
    return () => clearInterval(interval);
  }, [checkDaemonStatus]);

  return { online, lastHeartbeat };
}

function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  return (
    <button
      onClick={handleCopy}
      style={{
        padding: "2px 7px",
        fontSize: 10,
        fontWeight: 500,
        background: copied ? "var(--teal-tint)" : "transparent",
        color: copied ? "var(--teal-dark)" : "var(--ink-muted)",
        border: "1px solid " + (copied ? "var(--teal)" : "var(--line)"),
        borderRadius: 3,
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      {copied ? "✓" : label}
    </button>
  );
}

function CommandLine({ command }: { command: string }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        background: "var(--paper-2, #FAFAF7)",
        border: "1px solid var(--line)",
        borderRadius: 4,
        padding: "5px 8px",
        fontFamily: "monospace",
        fontSize: 12,
        color: "#1A1A1A",
      }}
    >
      <span style={{ flex: 1, wordBreak: "break-all" }}>{command}</span>
      <CopyButton text={command} />
    </div>
  );
}

function SetupStep({
  step,
  label,
  children,
}: {
  step: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          color: "#6b7280",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          marginBottom: 4,
        }}
      >
        Step {step} — {label}
      </div>
      {children}
    </div>
  );
}

function DaemonOnboardingGuide() {
  const [troubleshootOpen, setTroubleshootOpen] = useState(false);

  function scrollToAgentSection() {
    document
      .getElementById("team-section-ai")
      ?.scrollIntoView({ behavior: "smooth" });
  }

  return (
    <div className="sc-group-card" style={{ marginTop: 12 }}>
      {/* track:workspace-key-onboarding (decision #48253) — self-contained
          walkthrough (goal, prerequisites, numbered OS-aware steps,
          expected result, verification, troubleshooting, safety) for an AI
          assistant to read and use. Never contains a key or URL — see
          src/lib/copyInstructions.ts's own header comment. */}
      <div style={{ marginBottom: 12 }}>
        <CopyInstructionsButton text={buildDaemonInstallInstructions()} />
      </div>

      {/* Steps */}
      <SetupStep step="1" label="Install the Relay daemon (one time setup)">
        <CommandLine command="relay install" />
        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6 }}>
          When it asks for a key, paste your personal key from My Daemon Key above.
        </div>
      </SetupStep>

      <SetupStep step="2" label="Run in a terminal window. Keep it open while agents work.">
        <CommandLine command="relay start" />
        <div style={{ fontSize: 11, color: "#92400e", marginTop: 6 }}>
          ⚠️ Do not let your computer sleep — sleeping pauses all agent activity.
        </div>
      </SetupStep>

      <SetupStep step="3" label="Connect your AI agent">
        <button
          onClick={scrollToAgentSection}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "4px 10px",
            fontSize: 12,
            fontWeight: 500,
            color: "#1A6B6B",
            background: "transparent",
            border: "1px solid #1A6B6B33",
            borderRadius: 4,
            cursor: "pointer",
          }}
        >
          Go to Agent setup ↓
        </button>
      </SetupStep>

      {/* Commands reference */}
      <div
        style={{
          fontSize: 12,
          color: "#6b7280",
          lineHeight: 1.6,
          paddingTop: 4,
          borderTop: "1px solid #f3f4f6",
        }}
      >
        <strong style={{ color: "#374151", display: "block", marginBottom: 8 }}>
          Commands
        </strong>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <div style={{ marginBottom: 3 }}>
              <code style={{ background: "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>
                relay start
              </code>{" "}
              — Start the daemon. Run this when you begin a work session.
            </div>
            <CommandLine command="relay start" />
          </div>
          <div>
            <div style={{ marginBottom: 3 }}>
              <code style={{ background: "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>
                relay stop
              </code>{" "}
              — Stop the daemon. Run this when you're done for the day.
            </div>
            <CommandLine command="relay stop" />
          </div>
          <div>
            <div style={{ marginBottom: 3 }}>
              <code style={{ background: "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>
                relay logs
              </code>{" "}
              — View daemon activity and agent output.
            </div>
            <CommandLine command="relay logs" />
          </div>
        </div>
      </div>

      {/* Session hygiene */}
      <div
        style={{
          fontSize: 12,
          color: "#6b7280",
          lineHeight: 1.6,
          paddingTop: 4,
          borderTop: "1px solid #f3f4f6",
        }}
      >
        <strong style={{ color: "#374151", display: "block", marginBottom: 4 }}>
          Daily routine
        </strong>
        Stop the daemon when you finish working and restart at the beginning of
        each new session. This ensures agents start fresh and any interrupted
        work is automatically resumed.
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
          <div>
            <span style={{ color: "#374151", fontWeight: 500 }}>Start of session:</span>{" "}
            <code style={{ background: "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>
              relay start
            </code>
          </div>
          <div>
            <span style={{ color: "#374151", fontWeight: 500 }}>End of session:</span>{" "}
            <code style={{ background: "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>
              relay stop
            </code>
          </div>
          <div>
            <span style={{ color: "#374151", fontWeight: 500 }}>
              If something seems stuck:
            </span>{" "}
            <code style={{ background: "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>
              relay stop
            </code>
            , then{" "}
            <code style={{ background: "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>
              relay start
            </code>
          </div>
        </div>
      </div>

      {/* Why keep it running */}
      <div
        style={{
          fontSize: 12,
          color: "#6b7280",
          lineHeight: 1.6,
          paddingTop: 4,
          borderTop: "1px solid #f3f4f6",
        }}
      >
        <strong style={{ color: "#374151" }}>Why keep it running?</strong>{" "}
        The daemon coordinates your AI agents. It runs on your computer so agents
        can access your codebase. Keep the terminal window open while you're
        working.
        <div style={{ marginTop: 8, color: "#92400e" }}>
          ⚠️ <strong>Prevent sleep</strong> — Agent work pauses if your computer
          goes to sleep or the terminal closes. Keep your computer awake during
          active work sessions.
          <br />
          macOS tip: Run{" "}
          <code style={{ background: "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>
            caffeinate -i
          </code>{" "}
          in a separate terminal to prevent sleep.
        </div>
      </div>

      {/* Troubleshooting */}
      <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 4 }}>
        <button
          onClick={() => setTroubleshootOpen((v) => !v)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            background: "none",
            border: "none",
            padding: 0,
            fontSize: 12,
            fontWeight: 500,
            color: "#6b7280",
            cursor: "pointer",
          }}
        >
          <span className={`caret${troubleshootOpen ? " is-open" : ""}`} aria-hidden="true">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M3 2 L7 5 L3 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          Troubleshooting
        </button>
        {troubleshootOpen && (
          <ul
            style={{
              margin: "8px 0 0 0",
              padding: "0 0 0 16px",
              fontSize: 12,
              color: "#374151",
              lineHeight: 1.8,
            }}
          >
            <li>
              <strong>Daemon stopped?</strong> Run{" "}
              <code
                style={{
                  background: "#f3f4f6",
                  padding: "1px 4px",
                  borderRadius: 3,
                }}
              >
                relay start
              </code>
            </li>
            <li>
              <strong>Auth error?</strong> Ensure you're authenticated with your
              cloud provider (e.g.{" "}
              <code
                style={{
                  background: "#f3f4f6",
                  padding: "1px 4px",
                  borderRadius: 3,
                }}
              >
                gcloud auth application-default login
              </code>{" "}
              for GCP), then run{" "}
              <code
                style={{
                  background: "#f3f4f6",
                  padding: "1px 4px",
                  borderRadius: 3,
                }}
              >
                relay start
              </code>
            </li>
            <li>
              <strong>Nothing happening?</strong> Make sure the terminal window
              is still open and try posting @RELAY in the Chat to test
              the connection.
            </li>
            <li>
              <strong>Work stopped mid-sprint?</strong> Your computer may have gone
              to sleep. Run{" "}
              <code
                style={{
                  background: "#f3f4f6",
                  padding: "1px 4px",
                  borderRadius: 3,
                }}
              >
                relay stop
              </code>{" "}
              then{" "}
              <code
                style={{
                  background: "#f3f4f6",
                  padding: "1px 4px",
                  borderRadius: 3,
                }}
              >
                relay start
              </code>{" "}
              to reconnect.
            </li>
          </ul>
        )}
      </div>
    </div>
  );
}

function SchedulerCard() {
  return (
    <div className="sc-item-row">
      <div>
        <div className="sc-item-name">Scheduler</div>
        <div className="sc-item-meta">Managed by Relay — always on</div>
      </div>
    </div>
  );
}

interface SystemTeamSectionProps {
  workspaceId: string;
}

export function SystemTeamSection({ workspaceId }: SystemTeamSectionProps) {
  const { online, lastHeartbeat } = useDaemonStatus(workspaceId);

  const lastSeenText = lastHeartbeat
    ? (() => {
        const seconds = Math.floor(
          (Date.now() - new Date(lastHeartbeat).getTime()) / 1000
        );
        if (seconds < 60) return `${seconds}s ago`;
        const minutes = Math.floor(seconds / 60);
        return `${minutes}m ago`;
      })()
    : null;

  return (
    <section id="team-section-system" style={{ marginBottom: 32 }}>
      <div className="sc-section-label">System Team</div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <SchedulerCard />

        {/* Daemon status card */}
        <div className="sc-item-row">
          <div>
            <div className="sc-item-name" style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <span
                style={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: online ? "var(--teal-dark)" : "var(--berry)",
                  flexShrink: 0,
                }}
              />
              Relay Daemon
            </div>
            <div className="sc-item-meta">
              {online
                ? lastSeenText
                  ? `Daemon online — last heartbeat ${lastSeenText}`
                  : "Daemon online"
                : "Relay Helper is offline. Open Relay Helper and click Start (or run relay start)."}
            </div>
          </div>

          {online && (
            <span
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 500,
                color: "var(--teal-dark)",
                background: "var(--teal-tint)",
                border: "1px solid var(--teal)",
                borderRadius: 4,
                padding: "2px 8px",
              }}
            >
              Running
            </span>
          )}
        </div>
      </div>

      {/* Always-visible daemon onboarding guide */}
      <DaemonOnboardingGuide />
    </section>
  );
}
